import React, { useState, useEffect, useRef, useMemo } from 'react';
import { EyeOff, RefreshCw, Flame, Eye, Heart, Mail, Tag } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchOffMarketDemand } from '../api/analyticsApi';

const scoreColor = (v) =>
    v == null ? '#94a3b8' : v >= 65 ? '#dc2626' : v >= 35 ? '#d97706' : '#2563eb';

const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
            <Icon className="w-4 h-4" style={{ color }} /> {label}
        </div>
        <div className="text-2xl font-bold text-gray-900">{(value ?? 0).toLocaleString()}</div>
    </div>
);

/**
 * Off-Market Demand Index — "Most Wanted Off-Market Areas".
 * Ranks suburbs by real off-market buyer demand (views / saves / enquiries /
 * offers on off-market stock). The client's moat report.
 */
const OffMarketDemandPanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const tokenRef = useRef(0);

    useEffect(() => {
        const myToken = ++tokenRef.current;
        setLoading(true);
        fetchOffMarketDemand(20)
            .then((res) => { if (myToken === tokenRef.current) setData(res); })
            .catch((err) => {
                if (myToken !== tokenRef.current) return;
                console.error('Off-market demand load failed:', err);
                toast.error('Failed to load off-market demand index');
            })
            .finally(() => { if (myToken === tokenRef.current) setLoading(false); });
    }, []);

    const totals = data?.totals || {};
    const rows = useMemo(() => data?.results || [], [data]);
    const maxScore = useMemo(() => Math.max(1, ...rows.map((r) => r.demand_score || 0)), [rows]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-12 text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading off-market demand…
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                    <EyeOff className="w-5 h-5 text-violet-600" /> Off-Market Demand Index
                </h2>
                <p className="text-sm text-gray-500">
                    Where buyers are chasing stock that isn't publicly advertised — ranked by real views,
                    saves, enquiries &amp; offers on off-market listings.
                </p>
            </div>

            {/* Headline */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <StatCard icon={EyeOff} label="Off-market listings" value={totals.off_market_listings} color="#7c3aed" />
                <StatCard icon={Eye} label="Views 30D" value={totals.views_30d} color="#6366f1" />
                <StatCard icon={Heart} label="Saves 30D" value={totals.saves_30d} color="#e11d48" />
                <StatCard icon={Mail} label="Enquiries 30D" value={totals.enquiries_30d} color="#0891b2" />
                <StatCard icon={Tag} label="Offers" value={totals.offers} color="#16a34a" />
            </div>

            {/* Most Wanted Off-Market Areas */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                    <Flame className="w-4 h-4 text-violet-600" />
                    <h3 className="text-sm font-semibold text-gray-900">Most Wanted Off-Market Areas</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[720px]">
                        <thead>
                            <tr className="border-b border-gray-100 text-[10px] uppercase tracking-widest text-gray-400">
                                <th className="text-center py-3 px-2 w-10">#</th>
                                <th className="text-left py-3 px-4">Suburb</th>
                                <th className="text-right py-3 px-3">Demand</th>
                                <th className="text-right py-3 px-3">Listings</th>
                                <th className="text-right py-3 px-3">Views</th>
                                <th className="text-right py-3 px-3">Saves</th>
                                <th className="text-right py-3 px-3">Enquiries</th>
                                <th className="text-right py-3 px-3">Offers</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {rows.map((r, i) => (
                                <tr key={`${r.suburb}:${r.state}`} className="hover:bg-violet-50/40 transition-colors">
                                    <td className="text-center px-2 py-3 text-gray-400">{r.rank ?? i + 1}</td>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold text-gray-800">{r.suburb}</div>
                                        <div className="text-xs text-gray-400">{r.state}</div>
                                    </td>
                                    <td className="px-3 py-3">
                                        <div className="flex items-center justify-end gap-2">
                                            <div className="w-16 bg-gray-100 rounded-full h-2 overflow-hidden hidden sm:block">
                                                <div className="h-2 rounded-full"
                                                     style={{ width: `${((r.demand_score || 0) / maxScore) * 100}%`, background: scoreColor(r.demand_score) }} />
                                            </div>
                                            <span className="font-bold tabular-nums w-7 text-right" style={{ color: scoreColor(r.demand_score) }}>
                                                {r.demand_score ?? '—'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 text-right text-gray-700">{(r.off_market_listings ?? 0).toLocaleString()}</td>
                                    <td className="px-3 py-3 text-right text-gray-700">{(r.views_30d ?? 0).toLocaleString()}</td>
                                    <td className="px-3 py-3 text-right text-gray-700">{(r.saves_30d ?? 0).toLocaleString()}</td>
                                    <td className="px-3 py-3 text-right text-gray-700">{(r.enquiries_30d ?? 0).toLocaleString()}</td>
                                    <td className={`px-3 py-3 text-right font-semibold ${(r.offers ?? 0) > 0 ? 'text-emerald-700' : 'text-gray-300'}`}>
                                        {(r.offers ?? 0).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                            {!rows.length && (
                                <tr><td colSpan={8} className="text-center text-gray-400 py-10">No off-market demand recorded yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                    <p className="text-[10px] text-gray-400">
                        Demand score blends off-market views, saves, enquiries and offers per listing (0–100).
                        Views / saves / enquiries are last 30 days; offers are all-time. Nobody else has this data.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default OffMarketDemandPanel;
